<template>
    <div :class="statusBuilder('RPSidebar', status)">
        <div
            v-for="section in sections"
            :key="JSON.stringify(section.header)"
            :class="[
                'RPSidebar__section',
                showIcon && 'RPSidebar__section--icon'
            ]"
        >
            <StreamlineIcon
                v-if="showIcon && hasIcon(section.header)"
                :icon="section.header.icon"
                :stroke="grey200"
                :size="16"
            />
            <span
                :class="[
                    'RPSidebar__header',
                    showIcon&&'RPSidebar__header--icon' ]"
            >{{ section.header.label }}</span>
            <Tab
                v-for="tab in section.tabs"
                :key="tab.label"
                :class="[ showIcon && 'RPSidebar__tab--icon' ]"
                :label="tab.label"
                :to="tab.to"
                :sub-tabs="tab.subTabs"
                :active-tab="activeTab"
                :dynamic-tab="dynamicTab"
                @navigate="to => $emit('navigate', to)"
            />
        </div>
        <Tab
            v-for="tab in tabs"
            :key="tab.label"
            :label="tab.label"
            :to="tab.to"
            :sub-tabs="tab.subTabs"
            :active-tab="activeTab"
            :dynamic-tab="dynamicTab"
            @navigate="to => $emit('navigate', to)"
        />
    </div>
</template>

<script>
import stat from 'status/index';
import statusBuilder from 'status/statusBuilder';
import Tab from './SideBar/Tab.vue';
import {has} from 'lodash';
import StreamlineIcon from "@reviewpush/streamline-icons-vue";
import {grey200} from 'style/_colors.scss';

export default {
    name: 'RPSidebar',
    components: {
        Tab,
        StreamlineIcon
    },
    props: {
        /**
         * A list of tabs to display. See description for more detail
         */
        tabs: {
            type: Array,
            default: () => ([])
        },
        sections: {
            type: Array,
            default: () => ([])
        },
        /**
         * The route name for the currently active tab
         */
        activeTab: {
            type: String,
            required: true,
        },
        /**
         * Displays text underneath the currently active tab. Useful to hint when
         * the user has a current subpage/section open
         */
        dynamicTab: {
            type: String,
            default: '',
        },
        status: {
            type: String,
            default: stat.STANDBY,
        },
    },
    data() {
        return {
            grey200
        };
    },
    computed: {
        showIcon() {
            return !!this.sections.filter(s => has(s.header, 'icon')).length;
        }
    },
    methods: {
        statusBuilder,
        hasIcon(header){
            return has(header, 'icon');
        },
    }
};
</script>

<style lang="scss">
    .RPSidebar {
        padding: 1rem;
        background: $white;
        border-radius: 4px;
        display: flex;
        flex-direction: column;
        box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.08);
        margin-bottom: auto;

        @include loading(21rem, 100%);

        &__header {
            margin: 0 0 0.5rem 0.5rem;
            color: $text-dark;
            font-weight: 600;
            &--icon {
                grid-column: 2;
            }
        }
        &__section {
            display: grid;
            grid-template-columns: auto;
            &--icon {
                grid-template-columns: 1rem auto;
            }
            &:not(:last-child) { margin-bottom: 0.5rem; }

        }
        &__tab--icon{ grid-column: 2;}
    }
</style>

<docs>
To display a list of tabs, pass through an array of tab objects. A tab object can be one of two types:

1. Basic Tabs

    These are plain links with no nested tabs underneath them. They require a `label` (text) property and a `to` property
    containing a route object (only the `name` property is required on the route object).

    Ex:

    ```json static
    {
        label: 'First Tab Label',
        to: {
            name: 'SomeRouteName'
        },
    }
    ```

2. Tab with subtabs

    These tabs have a `header` property (text) and a `subTabs` property (array of objects). The `subTabs` array contains
    basic tab objects.

    Ex:

    ```json static
    {
        header: 'First Tab Header',
        subTabs: [
            {
                label: 'A Subtab',
                to: {
                    name: 'SomeSubRoute'
                }
            }
        ]
    }
    ```

```vue
import {FolderText} from '@reviewpush/streamline-regular/lib/files-folders/Folders';
<RPSidebar
    :active-tab="myValString"
    :tabs="[{'to':{'name':'UserNotificationsSettings'},'label':'Notifications'},{'to':{'name':'UserReports'},'label':'Reports'},{'to':{'name':'MyProfile'},'label':'My Profile'}]"
    @navigate="nav => myValString = nav.name"
>
</RPSidebar>
<br>
<RPSidebar
    :active-tab="myValString"
    :sections="[
        {
            'header':{
                label: 'Collective Reports',
                icon: FolderText,
            },
            'tabs':[
                {'to':{'name':'OverviewReport'},'label':'Overview'},
                {'to':{'name':'LocationsReport'},'label':'Locations'},
                {'to':{'name':'SentimentReport'},'label':'Sentiment'},
                {'to':{'name':'CompetitorsReport'},'label':'Competitors'}
            ]
        },
        {
            'header':{
                label: 'Focused Reports',
                icon: FolderText,
            },
            'tabs':[
                {'to':{'name':'ReviewsReport'},'label':'Reviews'},
                {'to':{'name':'RatingsReport'},'label':'Ratings'},
                {'to':{'name':'KeywordsReport'},'label':'Keywords'},
                {'to':{'name':'LandingPagesReport'},'label':'Landing Pages'},
                {'to':{'name':'CampaignsReport'},'label':'Campaigns'},
                {'to':{'name':'ReviewResponsesReport'},'label':'Review Responses'}
            ]
        }
    ]"
    @navigate="nav => myValString = nav.name"
>
</RPSidebar>
<br>
<RPSidebar
    active-tab="SampleDynamic"
    dynamic-tab="Sample Dynamic"
    :sections="[
        {
            'header':{ label: 'General' },
            'tabs':[
                {'to':{'name':'GlobalNotifications'},'label':'Notifications'},
                {'to':{'name':'Billing'},'label':'Billing'}
            ]
        },
        {
            'header':{ label: 'Company' },
            'tabs':[
                {
                    'to':{'name':'BrandsRegionsTable'},
                    'label':'Brands/Regions',
                    'subTabs':[
                        {'to':{'name':'SampleDynamic'}}
                    ],
                },
                {'to':{'name':'UsersTable'},'label':'Users'},
                {'to':{'name':'UserRolesTable'},'label':'User Roles'},
                {'to':{'name':'ThirdPartyLoginsTable'},'label':'3rd Party Accounts'}
            ]
        },
        {
            'header':{ label: 'Product Tools' },
            'tabs':[
                {'to':{'name':'CompanyReports'},'label':'Reports'},
                {'to':{'name':'LandingPagesTable'},'label':'Landing Pages'},
                {'to':{'name':'SuggestedResponseTable'},'label':'Suggested Responses'},
                {'to':{'name':'EmbeddedReviewsTable'},'label':'Embedded Reviews'},
                {'to':{'name':'ReviewPushAPI'},'label':'ReviewPush API'}
            ]
        }
    ]"
    @navigate="nav => myValString = nav.name"
>
</RPSidebar>
<br>
<RPSidebar
    :active-tab="myValString"
    :tabs="[
        {
            'key': 'overview',
            'label': 'Overview',
            'to': { 'name': 'LocationOverview'  }
        },
        {
            'key': 'reviews',
            'label': 'Reviews',
            'to': {'name': 'LocationReviews' }
        },
        {
            'key': 'profiles',
            'label': 'Profiles',
            'to': {'name': 'LocationProfiles' }
        },
        {
            'key': 'campaigns',
            'label': 'Campaigns',
            'to': { 'name': 'LocationCampaigns' }
        },
        {
            'key': 'competitors',
            'label': 'Competitors',
            'to': {'name': 'LocationCompetitorsOverview'}
        },
        {
            'key': 'settings',
            'label': 'Settings',
            'to': { 'name': 'LocationInformation' },
            'subTabs': [
                {
                    'label': 'Information',
                    'key': 'information',
                    'to': {'name': 'LocationInformation'}
                },
                {
                    'label': 'Notifications',
                    'key': 'notifications',
                    'to': { 'name': 'LocationNotifications' }
                },
                {
                    'label': 'Competitors',
                    'key': 'competitors',
                    'to': {'name': 'LocationCompetitors' }
                },
                {
                    'label': 'Branding',
                    'key': 'branding',
                    'to': { 'name': 'LocationBranding'  }
                },
                {
                    'label': 'Landing Pages',
                    'key': 'landingpages',
                    'to': {  'name': 'LocationLandingPagesTable'  }
                },
                {
                    'label': 'Embedded Feeds',
                    'key': 'embedded',
                    'to': {'name': 'LocationEmbeddedReviewsTable'  }
                }
            ]
        }
    ]"
    @navigate="nav => myValString = nav.name"
>
</RPSidebar>
```
</docs>